/**
 * 在素材渲染后，将素材包的数据加工成渲染需要的数据
 */
import qc, { Node } from 'qc'
import { IframeWindow } from '@ps/bridge/iframe/iframe.window.type'
import { each } from '@ps/bridge/qc/node/utils'
import { isImage, isSequenceFrame, isText } from '@ps/bridge/qc/base'
import store from '@ps/store'
import _ from 'lodash'
import { defaultLanguage } from '@ps/workbench/config'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import {
  EditorDataScript,
  nodeOrigin,
  nodeTypes,
} from '@ps/bridge/qc/scripts/editor'
import * as aspectRatioFitter from '@ps/bridge/qc/scripts/aspect-ratio-fitter'
import { aspectRatioFitterData } from '@ps/bridge/qc/scripts/aspect-ratio-fitter'
import { specialNodes } from '@ps/bridge/qc/node/config'
import filepath from '@ps/bridge/path'
import * as globalConfigScript from '@ps/bridge/qc/scripts/bg-config'
import * as movieClipScript from '@ps/bridge/qc/scripts/movie-clip'
import { addTempTextNodes } from '@ps/workbench/adapter/languages'
import {
  getTextKeysWhichNotRelatedToTextNode,
  cookTextNode,
} from '@ps/workbench/adapter/text'
import { IGlobalConfigBg } from '@ps/bridge/qc/scripts/bg-config'
import { adaptBgmWhenGameReady } from './bgm'
import {
  adaptBgImgWhenGameNodeHasNotGlobalConfigScript,
  adaptBgImgWhenGamePlayNodeHasGlobalConfigScript,
} from './bg-img'
import { getLanguagesFromGamePlay } from '@ps/workbench/language'

/**
 * 初始化 gamePlay 节点多语言相关信息
 */
function initLanguage({
  lang,
  langList,
  gamePlayNode,
}: {
  lang: string
  langList: string[]
  gamePlayNode: qc.Node
}) {
  const ped = editorScript.getScript(gamePlayNode) as EditorDataScript
  if (!ped.languageConfig.defaultLang) {
    ped.languageConfig.defaultLang = lang
  }
  if (!ped.languageConfig.langList) {
    ped.languageConfig.langList = langList
  }
}

export async function adaptGlobalConfig({
  qc,
  iframeWindow,
}: {
  qc: any
  iframeWindow: IframeWindow
}): Promise<void> {
  const { UIRoot } = iframeWindow
  const gamePlayNode = UIRoot.getChild(specialNodes.gamePlay.name)
  const hasGlobalConfigScript = Boolean(
    globalConfigScript.getScript(gamePlayNode, false),
  )
  let globalConfig: null | IGlobalConfigBg = null

  // 有 ps.GlobalConfigBg 脚本
  if (hasGlobalConfigScript) {
    console.log('有 ps.GlobalConfigBg 脚本，直接获取脚本')
    // 素材包的 ps.GlobalConfigBg 脚本跟 ps 的不一样，但是 ps 这边 ps.GlobalConfigBg 脚本后加载，所以会重写素材包的 ps.GlobalConfigBg 脚本
    globalConfig = globalConfigScript.getScript(gamePlayNode) as IGlobalConfigBg
    const data = await adaptBgImgWhenGamePlayNodeHasGlobalConfigScript({
      globalConfig,
      qc,
    })
    if (data && data.hasBgImg) {
      store.commit('workspace/editorConfig/updateEditorConfig', {
        bg: {
          value: 1,
          deleted: 0,
        },
      })
    }
  } else {
    console.log('无 ps.GlobalConfigBg 脚本，添加脚本')
    // 无 ps.GlobalConfigBg 脚本，添加脚本
    globalConfig = globalConfigScript.getScript(
      gamePlayNode,
      true,
    ) as IGlobalConfigBg
    const data = await adaptBgImgWhenGameNodeHasNotGlobalConfigScript({
      iframeWindow,
      qc,
    })
    if (data && data.globalConfig) {
      globalConfig = data.globalConfig
    }
    if (data && data.hasBgImg) {
      store.commit('workspace/editorConfig/updateEditorConfig', {
        bg: {
          value: 1,
          deleted: 0,
        },
      })
    }
  }

  // 处理 bgm 时，要保证 globalConfig 不为空
  const hasBgm = await adaptBgmWhenGameReady({
    iframeWindow,
    globalConfig: globalConfig as IGlobalConfigBg,
  })
  if (hasBgm) {
    store.commit('workspace/editorConfig/updateEditorConfig', {
      bgm: {
        value: 1,
        deleted: 0,
      },
    })
  }
}

function imageProcess(node: Node) {
  const nodeConfig = store.state.workspace.editorConfig.nodeConfig
  // 补充图片的素材路径
  if (!nodeConfig[node.uuid] || !nodeConfig[node.uuid].resource) {
    const img = _.get(node, 'texture.atlas.img')
    if (
      img &&
      typeof img.getAttribute === 'function' &&
      img.getAttribute('src')
    ) {
      store.dispatch('workspace/editorConfig/updateTargetNodeConfig', {
        resource: img.getAttribute('src'),
        originalResource: img.getAttribute('src'),
        node,
      })
    } else {
      console.error('无法找到该图片节点的图片资源', node)
    }
  }
}

function xlzProcess(node: Node) {
  const mv = movieClipScript.getScript(node)
  if (typeof mv === 'undefined') {
    return
  }
  // 基础包兼容
  // 自研的序列帧url有可能存在actionNames字段上
  if (_.isEmpty(mv.data) && !_.isEmpty(mv.actionNames)) {
    if (!mv.data) {
      mv.data = { allFramePlaySmart: '' }
    }
    const actionNames = mv.actionNames!
    const path = mv.path
    const totalUrls: string[] = []
    Object.keys(actionNames).forEach((key) => {
      const value = actionNames[key]
      const arr = []
      for (let i = 1; i <= Number(value); i++) {
        const mcData = mv.mcData
        const mcDataItem = mcData && mcData[key]
        let url = ''
        if (mcDataItem) {
          url = mcDataItem.frameName.replace('{0}', i.toString())
        } else {
          url = `${path}/${key}_${i}.bin`
        }

        arr.push(url)
        totalUrls.push(url)
      }
      mv.data[key] = arr.join(',')
    })
    mv.data['allFramePlaySmart'] = totalUrls.join(',')
  }
}

/**
 * 特性检测，特性检测通过后才允许做素材包适配
 * @param iframeWindow
 */
export function featureDetect(iframeWindow: IframeWindow) {
  const { UIRoot } = iframeWindow
  const gamePlayNode = UIRoot.getChild(specialNodes.gamePlay.name)
  if (!gamePlayNode) {
    throw new Error('缺少 gamePlay 节点')
  }

  if (
    UIRoot.children.find((node) => node.name !== specialNodes.gamePlay.name)
  ) {
    throw new Error('UIRoot 下缺少 gamePlay 节点')
  }

  if (!iframeWindow.qc) {
    throw new Error('缺少 qc')
  }

  if (!iframeWindow.ps) {
    throw new Error('缺少 ps')
  }

  const audioFunc = [
    'forceMuted',
    'stopAllSound',
    'recoveryAllSound',
    'stopBgSound',
    'playSoundEff',
    'stopSoundEff',
  ]
  const globalFunc = [...audioFunc, 'languagesMgr', 'MW_FONT_LOADER']
  const filteredFunc = globalFunc.filter((item) => !iframeWindow[item as any])
  if (!_.isEmpty(filteredFunc)) {
    throw new Error(`缺少 ${filteredFunc.join(',')} 全局函数`)
  }
}

export async function adaptNonNormMaterialDataWhenGameReady({
  qc,
  iframeWindow,
  pid,
}: {
  qc: any
  iframeWindow: IframeWindow
  pid: string
}) {
  const { UIRoot } = iframeWindow
  const gamePlayNode = UIRoot.getChild(specialNodes.gamePlay.name)
  const languagesRes = iframeWindow.languagesMgr.getRes()

  // 处理多语言
  // 优先使用 language.json 中的语言
  const langList = _.isEmpty(_.get(languagesRes, 'languages'))
    ? [defaultLanguage]
    : Object.keys(_.get(languagesRes, 'languages'))
  const currentLanguage =
    _.get(languagesRes, 'fallbackLocale') || defaultLanguage
  // 当前语言与语言列表是存储在 gamePlayNode 上的，如果 gameNode 没有多语言信息，则补充
  initLanguage({
    lang: currentLanguage,
    langList,
    gamePlayNode,
  })
  languagesRes.selfDevToPs = true

  // 处理场景节点
  const gamePlayChildren = gamePlayNode.children
  gamePlayChildren.forEach((node) => {
    // 有脚本则不需要再处理
    if (editorScript.getScript(node, false)) return
    const config = editorScript.getScript(node) as EditorDataScript
    // 添加场景节点说明
    config.$data.nodeType = nodeTypes.scene
    config.$data.nodeOrigin = nodeOrigin.import
  })

  // 处理场景节点之外的节点
  each(gamePlayNode, (node) => {
    if (editorScript.getScript(node, false)) return
    const config = editorScript.getScript(node) as EditorDataScript
    // 增加字段说明该节点是从基础包导入的
    config.$data.nodeOrigin = nodeOrigin.import

    const aspectRatioFitterData = aspectRatioFitter.getScript(
      node,
      false,
    ) as aspectRatioFitterData
    // 如果含有这个脚本，需要把 mode 设置为 0 才可以调整大小，不然会导致栈溢出
    if (aspectRatioFitterData) {
      aspectRatioFitterData.mode = 0
    }
  })

  const textKeysWhichNotRelatedToTextNode =
    getTextKeysWhichNotRelatedToTextNode({
      languagesRes,
      gamePlayNode,
    })
  const hasTempNode = !_.isEmpty(textKeysWhichNotRelatedToTextNode)
  const { gameOb } = qc
  const uuid2url = gameOb.assets._uuid2UrlConf
  // 为没有关联文本节点的配置新增一个临时节点
  // 文本节点处理流程：1、添加临时文本节点； 2、处理文本节点配置
  if (hasTempNode) {
    await addTempTextNodes({
      iframeWindow,
      pid,
      textKeys: textKeysWhichNotRelatedToTextNode,
      uuid2url,
    })
  }

  const eachCb = (node: Node) => {
    const utils = [
      {
        valid(node: Node) {
          return isText(node)
        },
        process(node: Node) {
          return cookTextNode({
            node,
            langList,
            languagesRes,
          })
        },
      },
      {
        valid(node: Node) {
          return isImage(node)
        },
        process: imageProcess,
      },
      {
        valid(node: Node) {
          return isSequenceFrame(node)
        },
        process: xlzProcess,
      },
    ]

    utils.forEach((item) => {
      if (item.valid(node)) {
        item.process(node)
      }
    })
  }
  each(gamePlayNode, eachCb)

  languagesRes.languages = getLanguagesFromGamePlay()

  if (_.get(iframeWindow, 'qc_game.phaser.cache.addJSON')) {
    iframeWindow.qc_game.phaser.cache.addJSON(
      'languages',
      filepath.LANGUAGES_JSON,
      languagesRes,
    )
  }

  await adaptGlobalConfig({
    qc,
    iframeWindow,
  })

  const gamePlayNodeEditorScript = editorScript.getScript(
    gamePlayNode,
  ) as EditorDataScript
  // 已适配标识
  gamePlayNodeEditorScript.$data.isAdapted = true
}
